import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { doCheckin, getCheckinStatus } from '@/api/interaction';

function getStreakLabel(streak: number): string {
  if (streak >= 30) return '坚持一个月';
  if (streak >= 14) return '两周不间断';
  if (streak >= 7) return '一周连签';
  if (streak >= 3) return '三日连签';
  return '第一步';
}

interface DailyCheckInProps {
  onGoPoints: () => void;
}

interface CheckinViewState {
  checkedToday: boolean;
  streakDays: number;
  totalDays: number;
  todayReward: number;
  featureEnabled: boolean;
}

const DEFAULT_STATE: CheckinViewState = {
  checkedToday: false,
  streakDays: 0,
  totalDays: 0,
  todayReward: 0,
  featureEnabled: false,
};

export default function DailyCheckIn({ onGoPoints }: DailyCheckInProps) {
  const { user, isLoggedIn } = useAuth();
  const [state, setState] = useState<CheckinViewState>(DEFAULT_STATE);
  const [hasSynced, setHasSynced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [animating, setAnimating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [rewardGranted, setRewardGranted] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const refreshStatus = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      setState(DEFAULT_STATE);
      setHasSynced(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await getCheckinStatus();
      setState({
        checkedToday: res.checkedToday,
        streakDays: res.streakDays,
        totalDays: res.totalDays,
        todayReward: res.todayReward,
        featureEnabled: res.featureEnabled,
      });
      setHasSynced(true);
    } catch (err) {
      console.error('Failed to fetch checkin status:', err);
      setErrorMsg(err instanceof Error ? err.message : '签到状态加载失败');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, user?.id]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    const handleRefresh = () => {
      void refreshStatus();
    };
    window.addEventListener('wanxiang_feature_updated', handleRefresh);
    window.addEventListener('wanxiang_points_updated', handleRefresh);
    return () => {
      window.removeEventListener('wanxiang_feature_updated', handleRefresh);
      window.removeEventListener('wanxiang_points_updated', handleRefresh);
    };
  }, [refreshStatus]);

  const handleCheckIn = useCallback(async () => {
    if (state.checkedToday || animating || !isLoggedIn) return;
    setAnimating(true);
    setErrorMsg('');
    try {
      const res = await doCheckin();
      setState((prev) => ({
        ...prev,
        checkedToday: true,
        streakDays: res.streakDays,
        totalDays: res.totalDays,
        todayReward: res.pointsAwarded,
      }));
      setRewardGranted(res.pointsAwarded);
      setShowSuccess(true);
      window.dispatchEvent(new CustomEvent('wanxiang_points_updated'));
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '签到失败，请稍后重试';
      setErrorMsg(msg);
    } finally {
      setAnimating(false);
    }
  }, [animating, isLoggedIn, state.checkedToday]);

  if (!state.featureEnabled) return null;

  const weekDays = ['一', '二', '三', '四', '五', '六', '日'];
  const today = new Date().getDay();
  const todayIdx = today === 0 ? 6 : today - 1;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 text-sm">每日签到</h3>
        </div>
        <div className="p-5 text-sm text-gray-400 flex items-center gap-2">
          <span className="w-4 h-4 border-2 border-gray-200 border-t-[#f97316] rounded-full animate-spin"></span>
          加载签到状态...
        </div>
      </div>
    );
  }

  if (!hasSynced && errorMsg) {
    return (
      <div className="bg-white rounded-2xl border border-red-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-red-50 flex items-center justify-between">
          <h3 className="font-bold text-red-500 text-sm">每日签到</h3>
        </div>
        <div className="p-5">
          <div className="mb-3 flex items-center gap-2 text-xs text-red-500 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
            <i className="ri-error-warning-line text-sm shrink-0"></i>
            <span>{errorMsg}</span>
          </div>
          <button
            onClick={() => { void refreshStatus(); }}
            className="w-full py-3 bg-[#f97316] text-white font-semibold text-sm rounded-xl hover:opacity-90 cursor-pointer whitespace-nowrap"
          >
            重新加载签到状态
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center bg-[#f97316]/10 rounded-lg">
            <i className="ri-calendar-check-line text-[#f97316] text-base"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">每日签到</h3>
            <p className="text-xs text-gray-400 mt-0.5">签到领灵石，连签奖励更多</p>
          </div>
        </div>
        <button
          onClick={onGoPoints}
          className="text-xs text-[#0ea5e9] hover:text-[#0284c7] cursor-pointer flex items-center gap-0.5 whitespace-nowrap"
        >
          灵石中心<i className="ri-arrow-right-s-line text-sm"></i>
        </button>
      </div>

      <div className="p-5">
        <div className="flex gap-1.5 mb-5">
          {weekDays.map((day, i) => {
            const isToday = i === todayIdx;
            const checkedThisSlot = isToday && state.checkedToday;
            return (
              <div
                key={day}
                className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl text-xs font-medium transition-all ${
                  isToday
                    ? state.checkedToday
                      ? 'bg-[#10b981]/10 border border-[#10b981]/30'
                      : 'bg-[#f97316]/10 border border-[#f97316]/30'
                    : checkedThisSlot
                    ? 'bg-gray-50 border border-gray-100'
                    : 'bg-gray-50/50 border border-gray-50'
                }`}
              >
                <span className={`${isToday ? (state.checkedToday ? 'text-[#10b981]' : 'text-[#f97316]') : checkedThisSlot ? 'text-gray-400' : 'text-gray-300'}`}>
                  {day}
                </span>
                <div className={`w-5 h-5 flex items-center justify-center rounded-full ${
                  isToday && state.checkedToday
                    ? 'bg-[#10b981]'
                    : isToday
                    ? 'bg-[#f97316]/20'
                    : checkedThisSlot
                    ? 'bg-gray-200'
                    : 'bg-gray-100'
                }`}>
                  {isToday && state.checkedToday ? (
                    <i className="ri-check-line text-white text-xs"></i>
                  ) : checkedThisSlot ? (
                    <i className="ri-check-line text-gray-400 text-xs"></i>
                  ) : isToday ? (
                    <i className="ri-star-line text-[#f97316] text-xs"></i>
                  ) : (
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: '连续签到', value: `${state.streakDays}天`, icon: 'ri-fire-line', color: '#f97316' },
            { label: '今日奖励', value: hasSynced ? `+${state.todayReward}灵石` : '--', icon: 'ri-sparkling-line', color: '#10b981' },
            { label: '累计天数', value: `${state.totalDays}天`, icon: 'ri-calendar-line', color: '#0ea5e9' },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50/70 rounded-xl p-3 text-center">
              <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1 rounded-lg" style={{ backgroundColor: `${s.color}15` }}>
                <i className={`${s.icon} text-xs`} style={{ color: s.color }}></i>
              </div>
              <div className="font-bold text-gray-900 text-sm">{s.value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {state.streakDays > 0 && (
          <div className="mb-4 flex items-center gap-2 text-xs text-[#f97316] bg-[#f97316]/5 border border-[#f97316]/15 rounded-xl px-3 py-2">
            <i className="ri-fire-fill text-sm"></i>
            <span>连续签到 <strong>{state.streakDays}</strong> 天 · {getStreakLabel(state.streakDays)}</span>
          </div>
        )}

        {showSuccess && rewardGranted !== null && (
          <div className="mb-4 flex items-center gap-2 text-xs text-[#10b981] bg-[#10b981]/8 border border-[#10b981]/20 rounded-xl px-3 py-2.5 animate-pulse">
            <i className="ri-checkbox-circle-fill text-sm shrink-0"></i>
            <span>签到成功！获得 <strong>+{rewardGranted}</strong> 灵石奖励</span>
          </div>
        )}

        {errorMsg && (
          <div className="mb-4 flex items-center gap-2 text-xs text-red-500 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
            <i className="ri-error-warning-line text-sm shrink-0"></i>
            <span>{errorMsg}</span>
          </div>
        )}

        {state.checkedToday ? (
          <div className="flex items-center justify-center gap-2 w-full py-3 bg-[#10b981]/10 text-[#10b981] font-semibold text-sm rounded-xl border border-[#10b981]/20">
            <i className="ri-checkbox-circle-fill"></i>
            今日已签到
          </div>
        ) : (
          <button
            onClick={handleCheckIn}
            disabled={animating}
            className="relative w-full py-3 bg-gradient-to-r from-[#f97316] to-[#ea6c0a] text-white font-semibold text-sm rounded-xl hover:opacity-90 cursor-pointer whitespace-nowrap transition-all overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {animating ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                签到中...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-calendar-check-line"></i>
                立即签到 · 领取 {hasSynced ? state.todayReward : '--'} 灵石
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
